import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from "next";
import Link from "next/link";
import Image from "next/image";
import urlBuilder from "@sanity/image-url";

import { AppShell } from "@chef/feature-app-shell";
import { Card } from "@chef/components";
import { BASE_64_1X1_GREY } from "@chef/constants";
import { isEmptyArray } from "@chef/utils/array";

import { ContentComponent } from "../components";
import { IArticle, getArticleData, getPaginatedArticlesData } from "../queries";
import { SanityQuery } from "../types";
import { useArticleData } from "../hooks/article";
import { getClient } from "../utils";

import { intl } from "./article.Intl";

export const ArticlePage = (
  props: InferGetStaticPropsType<typeof getStaticProps>,
) => {
  const [article] = useArticleData(props.article);

  if (!article) {
    return null;
  }

  const relatedArticles = article.relatedArticles;

  const client = getClient();

  return (
    <ContentComponent block={article.content}>
      {({ children, title, description, imageUrl, index }) => (
        <AppShell
          title={title}
          description={description}
          image={imageUrl}
          index={index}
        >
          <AppShell.Meta />
          <AppShell.Header />
          <AppShell.DiscountBanner />

          <AppShell.Main noPadding>
            {children}

            {relatedArticles && !isEmptyArray(relatedArticles) && (
              <div className="pb-4 page-padding md:pb-10">
                <h2>{intl.RELATED_ARTICLES}</h2>

                <div className="flex flex-col w-full gap-4 mt-4 md:flex-row">
                  {relatedArticles.map((article) => {
                    let imageSrc = BASE_64_1X1_GREY;

                    if (
                      article.content._type === "content" &&
                      article.content.seo?.image
                    ) {
                      imageSrc = urlBuilder(client)
                        .image(article.content.seo.image)
                        .width(400)
                        .fit("max")
                        .format("webp")
                        .url();
                    }

                    return (
                      <Link
                        className="flex flex-col self-stretch flex-1 rounded-b-md shrink-0"
                        href={intl.ARTICLE_PATH(article.slug.current)}
                      >
                        <Card noPadding className="grow">
                          <div className="relative self-stretch rounded-md aspect-video">
                            <Image
                              src={imageSrc}
                              alt={article.title}
                              className="object-cover"
                              sizes="(max-width: 768px) 100vw, (max-width: 1440px) 50vw, 33vw"
                              fill
                            />
                          </div>

                          <p className="p-4 text-lg lg:text-xl lg:p-6">
                            <strong className="line-clamp-2">
                              {article.title}
                            </strong>
                          </p>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </AppShell.Main>
          <AppShell.Footer />
          <AppShell.TabBar />
        </AppShell>
      )}
    </ContentComponent>
  );
};

export const getStaticProps: GetStaticProps<
  {
    draftMode: boolean;
    article: IArticle;
  },
  SanityQuery
> = async ({ params = {}, draftMode = false }) => {
  const article = await getArticleData(params.slug, { preview: draftMode });

  return {
    props: {
      draftMode,
      article,
    },
    revalidate: 3600,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const articles = await getPaginatedArticlesData();

  return {
    paths: articles.map((article) => ({
      params: {
        slug: article.slug.current,
      },
    })),
    fallback: "blocking",
  };
};
